/**
 * Deterministic source-language heuristic (ADR-012).
 *
 * Short business messages ("OK", "PO-4471 ✔") defeat statistical detectors, so we use script
 * analysis + small stop-word lists, fall back to the sender's own reading locale, and always let
 * the sender override. The result is shown in the UI; it is a hint, not a fact.
 */
import { primaryLanguage } from './locales.ts';

export interface Detection {
  locale: string;
  /** Text contains significant portions of more than one language. */
  mixed: boolean;
  /** false when we fell back to the sender's locale because there was too little signal. */
  confident: boolean;
}

// Characters whose simplified/traditional forms differ (small, high-frequency sample).
const TRAD_ONLY = new Set(
  Array.from('們這個對說時會來學國與為還開關發電車門問題處貨單價錢後產廠樣種點機質驗試寫讀聽買賣認識讓請謝標準線網數額號碼確達無據週給條'),
);
const SIMP_ONLY = new Set(
  Array.from('们这个对说时会来学国与为还开关发电车门问题处货单价钱后产厂样种点机质验试写读听买卖认识让请谢标准线网数额号码确达无据周给条'),
);

const SV_WORDS = new Set(
  'och är inte det att en ett jag vi ni du på för med som har kan ska till om vad hur när varför inga ingen inget också eller men tack hej leverans leveransen kommer behöver måste bara redan efter före fredag måndag tisdag onsdag torsdag lördag söndag vecka beställning offert pris priset'.split(' '),
);
const EN_WORDS = new Set(
  'the and is are not it that a an i we you on for with as have can will to if what how when why no none also or but thanks hello delivery need must only already after before friday monday tuesday wednesday thursday saturday sunday week order quote price please'.split(' '),
);

const URL_RE = /\b(?:https?:\/\/|www\.)\S+/giu;
const EMAIL_RE = /\S+@\S+\.\S+/gu;
const CODE_RE = /\b(?=[A-Za-z0-9-_.\/]*\d)(?=[A-Za-z0-9-_.\/]*[A-Za-z])[A-Za-z0-9][A-Za-z0-9-_.\/]*\b/gu;

export function detectLanguage(text: string, fallbackLocale: string): Detection {
  const cleaned = text.replace(URL_RE, ' ').replace(EMAIL_RE, ' ').replace(CODE_RE, ' ');

  let han = 0;
  let trad = 0;
  let simp = 0;
  for (const ch of cleaned) {
    if (/\p{Script=Han}/u.test(ch)) {
      han++;
      if (TRAD_ONLY.has(ch)) trad++;
      if (SIMP_ONLY.has(ch)) simp++;
    }
  }

  const words = cleaned.toLowerCase().match(/\p{Script=Latin}+/gu) ?? [];
  const latinChars = words.reduce((n, w) => n + w.length, 0);
  let sv = 0;
  let en = 0;
  for (const w of words) {
    if (SV_WORDS.has(w)) sv += 2;
    if (EN_WORDS.has(w)) en += 2;
    if (/[åäö]/u.test(w)) sv += 3;
  }

  const zhLocale = trad > simp ? 'zh-Hant' : simp > trad ? 'zh-Hans' : primaryLanguage(fallbackLocale) === 'zh' ? fallbackLocale : 'zh-Hans';
  let latinLocale: string | null = null;
  if (sv > en) latinLocale = 'sv-SE';
  else if (en > sv) latinLocale = 'en';
  else if (latinChars > 0) {
    const fb = primaryLanguage(fallbackLocale);
    latinLocale = fb === 'sv' ? 'sv-SE' : fb === 'en' ? 'en' : null;
  }

  // Han characters carry roughly one word each; weight them ~2 latin letters.
  const hanWeight = han * 2;
  const latinWeight = latinLocale ? latinChars : 0;

  if (hanWeight === 0 && latinWeight === 0) {
    return { locale: fallbackLocale, mixed: false, confident: false };
  }
  const mixed = hanWeight > 0 && latinWeight > 0 && Math.min(hanWeight, latinWeight) / Math.max(hanWeight, latinWeight) > 0.25;
  if (hanWeight >= latinWeight) {
    return { locale: zhLocale, mixed, confident: han >= 2 };
  }
  const confident = sv !== en || latinChars >= 12;
  return { locale: latinLocale ?? fallbackLocale, mixed, confident: confident && latinLocale !== null };
}
